import { EXERCISE_REMARK_LIMITS, type ExerciseRemarkDto, type ExerciseRemarkStatus } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { RemarkStatusBadge } from '../components/training/ExerciseRemarks';
import { LinkButton, TestBadge } from '../components/training/common';
import { Button, Card, CardBody, EmptyState, ErrorBlock, Field, LoadingBlock, Select, Textarea, useToast } from '../components/ui';

/**
 * Hinweise zu Uebungen fuer die Verantwortlichen der Trainingsplanung ("Verwalten"): offene und erledigte,
 * mit Link zur Uebung. Erledigt wird mit einer optionalen Antwort, die der Verfasser an der Uebung sieht.
 */

type Filter = ExerciseRemarkStatus | 'ALL';

function RemarkCard({ remark }: { remark: ExerciseRemarkDto }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [answering, setAnswering] = useState(false);
  const [answer, setAnswer] = useState('');

  const resolve = useMutation({
    mutationFn: () => api.post<ExerciseRemarkDto>(`/api/training/remarks/${remark.id}/resolve`, { answer: answer.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['training-remarks'] });
      await queryClient.invalidateQueries({ queryKey: ['training-remarks-open-count'] });
      await queryClient.invalidateQueries({ queryKey: ['training-exercise-remarks'] });
      toast.show('Hinweis erledigt');
      setAnswering(false);
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Der Hinweis konnte nicht erledigt werden', 'error'),
  });

  return (
    <li>
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <RemarkStatusBadge remark={remark} />
            <Link to={`/sport/uebungen/${remark.exerciseId}`} className="min-w-0 break-words font-medium text-brand-700 underline">
              {remark.exerciseTitle}
            </Link>
          </div>
          <p className="mt-1 text-sm text-slate-500">{remark.authorName} · {formatDateTime(remark.createdAt)}</p>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-800">{remark.text}</p>

          {remark.status === 'DONE' && (
            <p className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-2 text-sm text-slate-700">
              <span className="font-medium">Erledigt{remark.resolvedByName ? ` von ${remark.resolvedByName}` : ''}{remark.resolvedAt ? ` am ${formatDateTime(remark.resolvedAt)}` : ''}</span>
              {remark.answer ? <>: {remark.answer}</> : null}
            </p>
          )}

          {remark.status === 'OPEN' && !answering && (
            <Button className="mt-3" variant="secondary" size="sm" onClick={() => setAnswering(true)} aria-label={`Hinweis zu ${remark.exerciseTitle} als erledigt markieren`}>
              Als erledigt markieren
            </Button>
          )}
          {remark.status === 'OPEN' && answering && (
            <div className="mt-3 space-y-2">
              <Field label="Antwort an den Verfasser (optional)" hint={`${answer.length} von ${EXERCISE_REMARK_LIMITS.answer} Zeichen`}>
                {(id) => <Textarea id={id} rows={3} value={answer} maxLength={EXERCISE_REMARK_LIMITS.answer} onChange={(event) => setAnswer(event.target.value)} placeholder="z. B. Ist korrigiert, danke." />}
              </Field>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" loading={resolve.isPending} onClick={() => resolve.mutate()}>Erledigt</Button>
                <Button size="sm" variant="secondary" disabled={resolve.isPending} onClick={() => { setAnswering(false); setAnswer(''); }}>Abbrechen</Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </li>
  );
}

export function ExerciseRemarksPage(): JSX.Element {
  const [filter, setFilter] = useState<Filter>('OPEN');
  const list = useQuery({
    queryKey: ['training-remarks', filter],
    queryFn: () => api.get<ExerciseRemarkDto[]>('/api/training/remarks', { status: filter === 'ALL' ? undefined : filter }),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Hinweise zu Übungen"
        description="Rückmeldungen aus dem Verein zu Standardübungen und eigenen Übungen"
        scoped={false}
        actions={
          <>
            <span className="self-center"><TestBadge /></span>
            <LinkButton to="/sport/uebungen">Übungen</LinkButton>
          </>
        }
      />

      <div className="max-w-xs">
        <Field label="Anzeigen">
          {(id) => (
            <Select id={id} value={filter} onChange={(event) => setFilter(event.target.value as Filter)}>
              <option value="OPEN">Offene</option>
              <option value="DONE">Erledigte</option>
              <option value="ALL">Alle</option>
            </Select>
          )}
        </Field>
      </div>

      {list.isLoading && <LoadingBlock />}
      {list.error && <ErrorBlock error={list.error} />}
      {list.data && list.data.length === 0 && (
        <EmptyState title={filter === 'OPEN' ? 'Keine offenen Hinweise' : filter === 'DONE' ? 'Noch nichts erledigt' : 'Noch keine Hinweise'} description="Hinweise schreibt man in der Übungsansicht über „Hinweis zur Übung“." />
      )}
      {list.data && list.data.length > 0 && (
        <>
          <p className="text-sm text-slate-500" aria-live="polite">{list.data.length} {list.data.length === 1 ? 'Hinweis' : 'Hinweise'}</p>
          <ul className="space-y-3">
            {list.data.map((remark) => <RemarkCard key={remark.id} remark={remark} />)}
          </ul>
        </>
      )}
    </div>
  );
}
